import CSS from 'csstype';
import { debounce } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { borderRadius, Div, fontSizing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IProps {
  fullWidth?: boolean;
  handleOnChange: (text: string) => void;
  initialValue?: string;
  inputStyle?: CSS.Properties;
  maxLength?: number;
  minLength?: number;
  msDelay?: number; // add delay to debounce. default 0. recommended 300 or 500 if needing debounce
  placeholder?: string;
  width?: CSS.WidthProperty<string | 0> | undefined;
  isAutoFocused?: boolean;
  key: string; // MUST BE UNIQUE
  inputID: string;
  isDisabled?: boolean;
}

export const FormBuilderTextInput = ({
  fullWidth,
  handleOnChange,
  isAutoFocused,
  initialValue,
  maxLength = 524288, // true input default
  minLength = 0,
  msDelay = 0,
  placeholder,
  width = '240px',
  inputStyle,
  inputID,
  isDisabled = false,
}: IProps) => {
  const [input, setInput] = useState(initialValue || '');

  const debouncedOnChange = useMemo(() => {
    return debounce(handleOnChange, msDelay);
  }, [handleOnChange]);

  const onChange = (value: string) => {
    setInput(value);
    debouncedOnChange(value);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const clipboardData = e.clipboardData?.getData('Text') || '';
    onChange(clipboardData);
  };

  return (
    <Div style={{ width: fullWidth ? '100%' : width }}>
      <Div style={{ width: '100%' }}>
        <input
          autoFocus={isAutoFocused}
          className='form-builder-text-input'
          type='text'
          placeholder={placeholder}
          value={input}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(e.currentTarget.value);
          }}
          onPaste={onPaste}
          maxLength={maxLength}
          minLength={minLength}
          style={{
            background: publicaColors.white,
            boxShadow: 'none',
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: borderRadius.small,
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
            padding: '6px 30px 6px 12px',
            width: '100%',
            height: '32px',
            fontSize: fontSizing.small,
            ...inputStyle,
          }}
          id={`${inputID}-formbuilder-text-input`}
          disabled={isDisabled}
        />
      </Div>
      {input && (
        <Div
          onClick={() => onChange('')}
          style={{
            position: 'absolute',
            top: '7px',
            right: '12px',
            height: '16px',
          }}
        >
          <IconV2 name='close' color={publicaColors.gray} />
        </Div>
      )}
    </Div>
  );
};
